import React from 'react';
import ReactPlayer from 'react-player';
import VolumeOffIcon from '@material-ui/icons/VolumeOff';
import VolumeUpIcon from '@material-ui/icons/VolumeUp';
import CachedIcon from '@material-ui/icons/Cached';
import Tooltip from '@material-ui/core/es/Tooltip/Tooltip';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import {connect} from 'react-redux';
import {isMobileSafari, isOpera, isIOS} from 'react-device-detect';
import {checkBoothOwnerOrRepresentative, getVideoIdFromYoutubeUrl, preventDefaultDrag} from '../../../Utils/utils';
import Slider from '@material-ui/core/Slider';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import PauseIcon from '@material-ui/icons/Pause';
import FastRewindIcon from '@material-ui/icons/FastRewind';
import FastForwardIcon from '@material-ui/icons/FastForward';
import moment from 'moment';
import axios from '../../../store/axios-instance';
import {isWebview} from '../../../Utils/is-webview';

class Video extends React.Component {
    fullscreenTimerId = 0;
    state = {
        fullscreen: false,
        showRefreshButton: false,
        muteVideo: true,
        isVideoPlaying: true,
        progressTime: 0,
        connectTimestamp: null,
        thumbnailImage: '',
    };
    youtubePlayer = React.createRef();

    componentDidMount() {
        window.addEventListener('resize', this.setActiveElementsPositions);
        document.addEventListener('webkitfullscreenchange', this.toggleFullScreenEscKey, false);
        document.addEventListener('mozfullscreenchange', this.toggleFullScreenEscKey, false);
        document.addEventListener('fullscreenchange', this.toggleFullScreenEscKey, false);
        document.addEventListener('MSFullscreenChange', this.toggleFullScreenEscKey, false);
        this.setThumbnailImage();
        this.setState({connectTimestamp: moment.utc()});
    }

    componentDidUpdate(prevProps) {
        // update thumbnail everytime the organizer changes the videoUrl
        if (prevProps.exhibitor.booth.video !== this.props.exhibitor.booth.video) {
            this.setState(
                {
                    progressTime: 0,
                },
                () => {
                    this.setThumbnailImage();
                    this.youtubePlayer.current.seekTo(0, 'seconds');
                }
            );
        }
    }

    componentWillUnmount() {
        if (document.fullscreenElement) {
            document.exitFullscreen().then(this.exitFullscreen());
        }

        window.removeEventListener('resize', this.setActiveElementsPositions);

        document.removeEventListener('webkitfullscreenchange', this.toggleFullScreenEscKey, false);
        document.removeEventListener('mozfullscreenchange', this.toggleFullScreenEscKey, false);
        document.removeEventListener('fullscreenchange', this.toggleFullScreenEscKey, false);
        document.removeEventListener('MSFullscreenChange', this.toggleFullScreenEscKey, false);

        const {connectTimestamp} = this.state;
        if (connectTimestamp) {
            this.handleVideoExit(connectTimestamp, () => {
                this.setState({connectTimestamp: null});
            });
        }
    }

    handleVideoExit = (connectTimestamp) => {
        const {event, user} = this.props;
        const {owner} = event;

        const actionType = 'boothVideos';
        const isRepresentative = checkBoothOwnerOrRepresentative(this.props.exhibitor, this.props.user._id);
        let trackBoothStatistics = !isRepresentative;

        if (owner._id === user._id) {
            trackBoothStatistics = false;
            let findEventRoles = user.eventRoles.find((el) => el?.event?._id === event?._id);
            if (findEventRoles) {
                if (findEventRoles?.roles?.includes('participant')) {
                    trackBoothStatistics = true;
                }
            }
        }

        if (connectTimestamp && trackBoothStatistics) {
            const data = {
                userId: this.props.user._id,
                connectTimestamp: connectTimestamp,
                disconnectTimestamp: moment.utc(),
                actionType: actionType,
            };
            axios({
                method: 'post',
                url: '/exhibitor/' + this.props.exhibitor._id + '/booth-statistics',
                data: data,
            })
                .then((res) => {})
                .catch((err) => {});
        }
    };

    setThumbnailImage = () => {
        let videoId = getVideoIdFromYoutubeUrl(this.props.exhibitor.booth.video || '');
        this.setState({
            thumbnailImage: `https://img.youtube.com/vi/${videoId}/0.jpg`,
        });
    };

    toggleFullScreenEscKey = () => {
        let fullscreenElement =
            document.fullscreenElement ||
            document.webkitFullscreenElement ||
            document.mozFullscreenElement ||
            document.msFullscreenElement;
        if (!fullscreenElement) {
            this.exitFullscreen();
        }
    };

    toggleFullScreen = () => {
        if (document.fullscreenElement) {
            document
                .exitFullscreen()
                .then(() => this.exitFullscreen())
                .catch((err) => console.error(err));
        } else {
            document.documentElement.requestFullscreen().then(() => this.startFullscreen());
        }
    };

    startFullscreen = () => {
        this.setState({fullscreen: true});
        this.hideVideoButtons();

        document.body.classList.add('fullscreen-video');
        // document.getElementById('js-auditorium-page').classList.add('fullscreen');
        window.addEventListener('mousemove', this.mouseMove);
    };

    exitFullscreen = () => {
        this.setState({fullscreen: false});
        this.showVideoButtons();
        clearTimeout(this.fullscreenTimerId);

        document.body.classList.remove('fullscreen-video');
        // document.getElementById('js-auditorium-page').classList.remove('fullscreen');
        window.removeEventListener('mousemove', this.mouseMove);
    };

    videoHasEnded = () => {
        this.exitFullscreen();
        this.setState({videoEnded: true, muteVideo: false});
    };

    mouseMove = () => {
        clearTimeout(this.fullscreenTimerId);
        this.showVideoButtons();
        this.fullscreenTimerId = setTimeout(this.hideVideoButtons, 3000);
    };

    showVideoButtons = () => {
        if (document.getElementById('videoButtons')) {
            document.getElementById('videoButtons').classList.remove('hide');
        }
    };

    hideVideoButtons = () => {
        if (document.getElementById('videoButtons')) {
            document.getElementById('videoButtons').classList.add('hide');
        }
    };

    refreshPage = () => window.location.reload(true);

    isPlayingError = () =>
        this.setState({
            showRefreshButton: true,
        });

    handleProgressChange = (e, value) => {
        e.preventDefault();
        this.youtubePlayer.current.seekTo(value / 100.5, 'fraction');
    };

    handleProgressBar = (progress) => {
        this.setState({
            progressTime: parseFloat((progress?.played * 100).toFixed(1)),
        });
    };

    toggleMute = () => this.setState({muteVideo: !this.state.muteVideo});

    toggleRewind = () =>
        this.youtubePlayer.current.seekTo(Math.floor(this.youtubePlayer.current.getCurrentTime()) - 30, 'seconds');

    toggleForward = () =>
        this.youtubePlayer.current.seekTo(Math.floor(this.youtubePlayer.current.getCurrentTime()) + 30, 'seconds');

    togglePause = () =>
        this.setState({isVideoPlaying: !this.state.isVideoPlaying}, () => {
            if (this.state.isVideoPlaying) {
                this.youtubePlayer.current.getInternalPlayer().playVideo();
            } else {
                this.youtubePlayer.current.getInternalPlayer().pauseVideo();
            }
        });

    render() {
        const {exhibitor, translation} = this.props;
        const {fullscreen, muteVideo, showRefreshButton, thumbnailImage, isVideoPlaying, progressTime} = this.state;
        const hideFullscreenButton = isMobileSafari || isOpera || isIOS;

        return (
            <div onDragStart={preventDefaultDrag} className={'videoWrapper ' + (fullscreen ? 'fullscreen' : '')}>
                <div onDragStart={preventDefaultDrag} className="video-container">
                    <img
                        src={thumbnailImage}
                        alt="Video thumbnail"
                        className={`booth-thumbnail ${!isVideoPlaying && 'show'}`}
                    />
                    <ReactPlayer
                        ref={this.youtubePlayer}
                        url={exhibitor.booth.video}
                        className="react-player"
                        controls={false}
                        width="100%"
                        height="100%"
                        volume={muteVideo ? 0 : 1}
                        muted={muteVideo}
                        loop
                        playing
                        playsinline
                        playIcon={<></>}
                        onProgress={this.handleProgressBar}
                        onContextMenu={preventDefaultDrag}
                        onEnded={this.videoHasEnded}
                        onError={this.isPlayingError}
                    />
                    <Slider
                        classes={{root: `progress-bar ${!isVideoPlaying && 'hide'}`}}
                        onChange={this.handleProgressChange}
                        value={progressTime}
                        color="secondary"
                    />
                    <div onDragStart={preventDefaultDrag} className="auditoriumOverlayPlayer"></div>
                </div>
                {showRefreshButton ? (
                    <div onDragStart={preventDefaultDrag} className="video-error-container">
                        <p onDragStart={preventDefaultDrag} onClick={this.refreshPage}>
                            {translation?.videoPlayer.videoNotPlayingError}
                        </p>
                        <button onClick={this.refreshPage}>
                            <CachedIcon fontSize="inherit" />
                        </button>
                    </div>
                ) : null}
                <div onDragStart={preventDefaultDrag} className="video-actions-container" id="videoButtons">
                    {!isWebview() && (
                        <button onClick={this.toggleFullScreen} className={`${hideFullscreenButton ? 'd-none' : ''}`}>
                            {fullscreen ? (
                                <Tooltip title={translation?.videoPlayer.tooltipMinimize}>
                                    <FullscreenExitIcon fontSize="inherit" />
                                </Tooltip>
                            ) : (
                                <Tooltip title={translation?.videoPlayer.tooltipFullscreen}>
                                    <FullscreenIcon fontSize="inherit" />
                                </Tooltip>
                            )}
                        </button>
                    )}
                    <button onClick={this.toggleRewind} className="fullscreen-button mute-button">
                        <FastRewindIcon fontSize="inherit" />
                    </button>
                    <button onClick={this.togglePause} className="fullscreen-button mute-button">
                        {isVideoPlaying ? <PauseIcon fontSize="inherit" /> : <PlayArrowIcon fontSize="inherit" />}
                    </button>
                    <button onClick={this.toggleForward} className="fullscreen-button mute-button">
                        <FastForwardIcon fontSize="inherit" />
                    </button>
                    <button onClick={this.toggleMute}>
                        {muteVideo ? (
                            <Tooltip title={translation?.videoPlayer.tooltipUnmute}>
                                <VolumeOffIcon fontSize="inherit" />
                            </Tooltip>
                        ) : (
                            <Tooltip title={translation?.videoPlayer.tooltipMute}>
                                <VolumeUpIcon fontSize="inherit" />
                            </Tooltip>
                        )}
                    </button>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state) => {
    return {
        languages: state.languages,
        translation: state.languages.translations[state.languages.platformLanguage],
        defaultTranslation: state.languages.translations['en'],
        event: state.event.data,
        exhibitor: state.exhibitor.data,
        user: state.user.data,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {};
};

export default connect(mapStateToProps, mapDispatchToProps)(Video);
